import { Injectable, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { OrganizationSubscriptionService } from '../../Modules/Organizations/Services/organization-subscription.service';
import { OrganizationBillingService } from '../../Modules/Organizations/Services/organization-billing.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { InvoiceSummary } from '../../Models/backoffice/invoice-summary.model';
import { OrganizationSubscription } from '../../Models/backoffice/organization-subscription.model';
import { PaymentSummary } from '../../Models/backoffice/payment-summary.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { SubscriptionShowResponse } from '../../Interfaces/api/backoffice/subscription.interface';
import {
  InvoiceListResponse,
  PaymentAttentionResponse,
  PaymentListResponse,
} from '../../Interfaces/api/backoffice/billing-document.interface';

@Injectable()
export class OrganizationBillingFacade {
  private readonly _subscriptionService = inject(OrganizationSubscriptionService);
  private readonly _billingService = inject(OrganizationBillingService);
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _organizationId = signal<string>('');
  private readonly _invoiceStatus = signal<string>('');
  private readonly _invoicePage = signal<number>(FIRST_PAGE);
  private readonly _paymentStatus = signal<string>('');
  private readonly _paymentPage = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);

  private readonly _subscription = httpResource<LaravelDefaultResponse<SubscriptionShowResponse>>(
    () =>
      this._organizationId()
        ? this._subscriptionService.showRequest(this._organizationId())
        : undefined,
  );

  private readonly _invoices = httpResource<LaravelDefaultResponse<InvoiceListResponse>>(() =>
    this._organizationId()
      ? this._billingService.invoicesRequest(
          this._organizationId(),
          this._invoiceStatus(),
          this._invoicePage(),
          this._pageSize(),
        )
      : undefined,
  );

  private readonly _payments = httpResource<LaravelDefaultResponse<PaymentListResponse>>(() =>
    this._organizationId()
      ? this._billingService.paymentsRequest(
          this._organizationId(),
          this._paymentStatus(),
          this._paymentPage(),
          this._pageSize(),
        )
      : undefined,
  );

  private readonly _attention = httpResource<LaravelDefaultResponse<PaymentAttentionResponse>>(() =>
    this._organizationId() ? this._billingService.attentionRequest(this._organizationId()) : undefined,
  );

  readonly subscription = computed<OrganizationSubscription | null>(() =>
    this._subscriptionService.toSubscription(this._resourceState.valueOf(this._subscription)),
  );

  readonly subscriptionState = computed<MetricState>(() =>
    this._resourceState.metricState(this._subscription.status()),
  );

  readonly invoiceStatus = computed<string>(() => this._invoiceStatus());

  readonly invoicePage = computed<number>(() => this._invoicePage());

  readonly paymentStatus = computed<string>(() => this._paymentStatus());

  readonly paymentPage = computed<number>(() => this._paymentPage());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly invoices = computed<InvoiceSummary[]>(
    () => this._billingService.toInvoices(this._resourceState.valueOf(this._invoices)).items,
  );

  readonly invoiceTotal = computed<number>(
    () => this._billingService.toInvoices(this._resourceState.valueOf(this._invoices)).total,
  );

  readonly invoicesState = computed<MetricState>(() =>
    this._resourceState.metricState(this._invoices.status()),
  );

  readonly payments = computed<PaymentSummary[]>(
    () => this._billingService.toPayments(this._resourceState.valueOf(this._payments)).items,
  );

  readonly paymentTotal = computed<number>(
    () => this._billingService.toPayments(this._resourceState.valueOf(this._payments)).total,
  );

  readonly paymentsState = computed<MetricState>(() =>
    this._resourceState.metricState(this._payments.status()),
  );

  readonly attention = computed<PaymentAttentionResponse['attention'] | null>(() =>
    this._billingService.toAttention(this._resourceState.valueOf(this._attention)),
  );

  load(organizationId: string): void {
    this._organizationId.set(organizationId);
  }

  filterInvoices(status: string): void {
    this._invoiceStatus.set(status);
    this._invoicePage.set(FIRST_PAGE);
  }

  goToInvoicePage(page: number): void {
    this._invoicePage.set(Math.max(FIRST_PAGE, page));
  }

  filterPayments(status: string): void {
    this._paymentStatus.set(status);
    this._paymentPage.set(FIRST_PAGE);
  }

  goToPaymentPage(page: number): void {
    this._paymentPage.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._invoicePage.set(FIRST_PAGE);
    this._paymentPage.set(FIRST_PAGE);
  }

  reload(): void {
    this._subscription.reload();
    this._invoices.reload();
    this._payments.reload();
    this._attention.reload();
  }
}
